<script setup lang="ts">
import { MedicalSystemRtSystemDto } from '@/api/generator';
import Notification from '@/components/notifications/Notification.vue';
import { NotificationMessage } from '@/components/notifications/types';
import RTSystemTile from '@/components/RTSystemTile/RTSystemTile.vue';
import EntityScopeSelectionModal from '@/views/tasks/components/EntityScopeSelectionModal.vue';
import { useToggle } from '@vueuse/core';
import { useTranslation } from 'i18next-vue';
import { computed } from 'vue';
import { useDisplay } from 'vuetify';

const { t } = useTranslation();

const { mobile } = useDisplay();

interface EntityScopeSelectionProps {
  options: Array<MedicalSystemRtSystemDto>;
  excludedIds?: string[];
  value: string | undefined;
  showTrackItAlias?: boolean;
}

const props = defineProps<EntityScopeSelectionProps>();

const emits = defineEmits<{
  update: [string | undefined];
}>();

const [modalIsOpen, toggleModal] = useToggle(false);

function modalSelectedHandler(id: string) {
  modalIsOpen.value = false;
  emits('update', id);
}

const targetItem = computed(() => props.options.find((item) => props.value === item.id));

const filteredOptions = computed(() =>
  props.options
    .filter((modifiedSystemOption) => !props.excludedIds?.includes(modifiedSystemOption.id))
    .map((item) => {
      return { ...item, serialNumber: undefined };
    }),
);

const notification = computed<NotificationMessage | undefined>(() => {
  return props.value && modalIsOpen.value
    ? {
        type: 'warning',
        message: t('ChangeTargetSystemWarningMsg'),
        closable: false,
      }
    : undefined;
});

defineExpose({
  toggleModal,
});
</script>

<template>
  <div>
    <div :class="{ mobile: mobile }">
      <RTSystemTile
        v-if="targetItem"
        :item="{ ...targetItem, serialNumber: undefined }"
        :showTrackItAlias="showTrackItAlias"
        class="fit-content"
      />

      <p v-else class="text-body-1 text-primary-lighten-2 font-weight-semibold">
        {{ $t('AddMedicalSystemToJobMsg') }}
      </p>
    </div>

    <EntityScopeSelectionModal
      v-model="modalIsOpen"
      :title="$t('AddMedicalSystem')"
      :message="$t('AddMedicalSystemToJobMsg')"
      :selectedId="value"
      :options="filteredOptions"
      :showTrackItAlias="showTrackItAlias"
      @submit="modalSelectedHandler"
    >
      <template #notification>
        <Notification :value="notification" />
      </template>
    </EntityScopeSelectionModal>
  </div>
</template>
